import { useEffect, useState } from 'react'
import { api, ApiError, prettyPhone, type SearchResults } from '../lib/api'

/** One search box across the whole system.
 *
 *  PLAN.html §09: "One search box across the whole system. Not separate search
 *  inside each section. And it is used AFTER calls — reviewing, checking,
 *  reporting — not during them. Mid-call the dispatcher isn't searching; he's
 *  validating an address."
 *
 *  That last sentence is why this is its own screen rather than a field in the
 *  chrome of the dispatcher's working view: a box that sits above an active
 *  call invites use during one, which is the habit the plan is trying not to
 *  build.
 *
 *  EQUIPMENT IS HERE, AND LEAVING IT OUT WAS THE FAILURE §09 WARNS ABOUT
 *
 *  This block used to say equipment was absent on purpose because the module
 *  did not exist. It exists. `search.equipment` is a real leaf, it is seeded
 *  to every dispatcher, and `app/search.py` has been returning a populated
 *  `groups.equipment` all along — this screen listed five groups and dropped
 *  the sixth on the floor, so searching an asset tag rendered nothing found
 *  and printed "Nothing matched", for every role including the wildcard
 *  Administrator. That is the same lesson the old comment was trying to
 *  avoid teaching — search does not work — arriving on this side of the wire
 *  instead of the server's. */

const GROUPS: { key: keyof SearchResults['groups']; label: string }[] = [
  { key: 'callers', label: 'Callers' },
  { key: 'places', label: 'Addresses and places' },
  { key: 'incidents', label: 'Incidents' },
  { key: 'members', label: 'Members' },
  { key: 'calls', label: 'Call log' },
  { key: 'equipment', label: 'Equipment' },
]

// The Equipment screen's own words for the same five states, which are local
// to that module and cannot be imported from here. `in_transit` on screen is
// the database's vocabulary rather than anybody's, and two screens naming one
// state differently is worse than the copy — so if these are ever reworded,
// both places move together.
const STATE_LABEL: Record<string, string> = {
  available: 'In stock',
  out: 'Out',
  in_transit: 'Coming back',
  repair: 'Repair',
  lost: 'Lost',
}

function when(value: string | null): string {
  if (!value) return ''
  return new Date(value).toLocaleString([], {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    hour12: false,
  })
}

/** Where the item actually is, which is half of what an asset tag is asking.
 *
 *  Somebody typing PMP-03 into this box wants "where is PMP-03" answered; a
 *  row that names the pump and its state and stops there sends them to the
 *  Equipment screen to ask the second half. The three fields are exclusive by
 *  construction — migration 031's CHECK holds `location_id` non-NULL exactly
 *  while the item is on a shelf, and the loan join only produces a place or a
 *  borrower for an item that is out — so this reads as a chain and never has
 *  to choose between two competing answers.
 *
 *  Nothing is invented when all three are absent. An item in transit is in
 *  somebody's van, which is not a location, and a lost one is by definition
 *  nowhere; the state beside it has already said so, and a manufactured
 *  "unknown" would read as a fault rather than as the truth. */
function whereIs(
  r: NonNullable<SearchResults['groups']['equipment']>[number],
): string | null {
  if (r.location_name) return r.location_name
  if (r.place_label) return `at ${r.place_label}`
  if (r.borrower_name) return `with ${r.borrower_name}`
  return null
}

export function Search({
  onOpenCall,
}: {
  /** Open a CLOSED call under Past calls. Absent for a role without the tick,
   *  and such a role is not shown closed calls at all. */
  onOpenCall?: (id: string) => void
} = {}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setResults(null); return }
    // Debounced, not per keystroke. §09 puts this after calls rather than
    // during them, so there is no need to race the typist — and a query per
    // character would be five cross-table searches to answer one question.
    let alive = true
    const t = setTimeout(async () => {
      setBusy(true)
      try {
        const found = await api.search(term)
        if (alive) { setResults(found); setError(null) }
      } catch (e) {
        if (alive) setError(e instanceof ApiError ? e.message : 'Search failed.')
      } finally {
        if (alive) setBusy(false)
      }
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [q])

  const groups = results?.groups ?? {}
  const found = GROUPS.filter((g) => (groups[g.key]?.length ?? 0) > 0)

  return (
    <div className="workspace pad">
      <h2>Search</h2>
      <p className="blurb">
        One box for the whole system — callers, addresses, incidents, members,
        the call log and equipment. A misspelled name or part of a number still
        lands: try the last four digits, or an asset tag.
      </p>

      <input
        className="searchbox"
        value={q}
        autoFocus
        placeholder="A name, a number, an address, INC-14…"
        onChange={(e) => setQ(e.target.value)}
      />

      {error && <div className="notice">{error}</div>}

      {results?.no_permissions && (
        <div className="listempty">
          Your role has no search permissions. An org admin can grant them
          under Settings → Roles.
        </div>
      )}

      {q.trim().length >= 2 && results && !busy
        && found.length === 0 && !results.no_permissions && (
        <div className="listempty">Nothing matched “{q.trim()}”.</div>
      )}

      {found.map((g) => (
        <div className="searchgroup" key={g.key}>
          <h3>{g.label} <span className="pill">{groups[g.key]!.length}</span></h3>

          {g.key === 'callers' && groups.callers!.map((r) => (
            <div className="listrow" key={r.id}>
              <span className="nm">{r.display_name ?? 'Unnamed'}</span>
              {r.phone_e164 && (
                <span className="mono chans">{prettyPhone(r.phone_e164)}</span>
              )}
              {r.phone_label && <span className="tag">{r.phone_label}</span>}
            </div>
          ))}

          {g.key === 'places' && groups.places!.map((r) => (
            <div className="listrow" key={r.id}>
              <span className="nm">{r.label}</span>
              {!r.located && <span className="tag">no map position</span>}
              {r.notes > 0 && (
                <span className="pill">{r.notes} site note{r.notes === 1 ? '' : 's'}</span>
              )}
            </div>
          ))}

          {g.key === 'incidents' && groups.incidents!.map((r) => (
            <div
              className={`listrow ${onOpenCall && r.status === 'closed' ? 'clickable' : ''}`}
              key={r.id}
              {...(onOpenCall && r.status === 'closed' ? {
                role: 'button',
                tabIndex: 0,
                title: 'Open this call under Past calls',
                onClick: () => onOpenCall(r.id),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenCall(r.id) }
                },
              } : {})}
            >
              <span className="mono">{r.number}</span>
              <span className="nm">{r.nature_label}</span>
              {r.place_label && <span className="chans">{r.place_label}</span>}
              <span className="tag">{r.status}</span>
              <span className="when">{when(r.called_at)}</span>
            </div>
          ))}

          {g.key === 'members' && groups.members!.map((r) => (
            <div className="listrow" key={r.id}>
              <span className="mono">{r.callsign ?? '—'}</span>
              <span className="nm">{r.display_name}</span>
              {!r.active && <span className="pill">retired</span>}
              {r.active && r.on_duty && <span className="tag">on duty</span>}
            </div>
          ))}

          {g.key === 'calls' && groups.calls!.map((r) => (
            <div className="listrow" key={r.id}>
              <span className="nm">{r.caller_name ?? 'Unknown number'}</span>
              <span className="tag">{r.disposition ?? 'unknown'}</span>
              {r.incident_id && <span className="pill">became a call</span>}
              <span className="when">{when(r.started_at)}</span>
            </div>
          ))}

          {/* The state is a plain `.tag`, like the incident's status and the
              call's disposition above it, and deliberately NOT `tag ${state}`.
              Those tinted variants live in pages/dispatch.css at the same
              specificity as theme.css's `.listrow .tag`, so which one won
              would come down to bundle import order — and they carry a fill
              with no padding rule in scope outside `table.report`, which is
              the "tint hugging the glyphs" the equipment strip already had to
              fix once. */}
          {g.key === 'equipment' && groups.equipment!.map((r) => (
            <div className="listrow" key={r.id}>
              <span className="mono">{r.asset_tag}</span>
              <span className="nm">{r.type_name}</span>
              <span className="tag">{STATE_LABEL[r.state] ?? r.state}</span>
              {whereIs(r) && <span className="chans">{whereIs(r)}</span>}
            </div>
          ))}
        </div>
      ))}

      <p className="footnote">
        Numbers are matched the same way the caller-ID pop matches them, so a
        result here and a pop can never disagree about whose number it is.
      </p>
    </div>
  )
}
